import {
  Box,
  Button,
  Inline,
  Skeleton,
  Tag,
  Typography,
} from '@omnipresentgroup/design-system';
import { toSentenceCase } from '@omnipresentgroup/helpers';
import type {
  ClientPolicy,
  ClientPolicyCategory,
  LeaveTypeEnum,
} from '@omnipresentgroup/types';
import { createColumnHelper } from '@tanstack/react-table';
import { useMemo } from 'react';

import { useManagerUser } from '../../../hooks';
import {
  LeaveSettingUpdateModal,
  StatutoryActivationModal,
} from '../components';

const EDIT_TEXT = 'Edit';
const ACTIVATE_TEXT = 'Activate';
const ACTIVE_TEXT = 'Active';
const INACTIVE_TEXT = 'Inactive';
const LEAVE_SETTING_VALUE_TEXT = (leaveType: string) =>
  `Employer ${leaveType} pay contributions`;

export enum EmploymentTimeClientPolicyActionItem {
  APPLY = 'Apply',
  EDIT = 'Edit',
  NONE = '',
}

export enum ColumnHeaders {
  label = 'Policy type',
  value = '',
  status = 'Status',
  action = '',
}

export type EmploymentTimeClientPolicyItem = {
  policy: ClientPolicy;
  label: ClientPolicyCategory;
  value: string;
  isApplied: boolean;
  action: EmploymentTimeClientPolicyActionItem;
  statutoryDescription: string;
};

export const useClientLeaveSettingTableConfig = (leaveType: LeaveTypeEnum) => {
  const columnHelper = useMemo(
    () => createColumnHelper<EmploymentTimeClientPolicyItem>(),
    [],
  );

  const { companyId } = useManagerUser();

  const columns = useMemo(
    () => [
      columnHelper.accessor('label', {
        cell: (info) => toSentenceCase(info.getValue() ?? ''),
        header: () => ColumnHeaders.label,
      }),
      columnHelper.accessor('value', {
        cell: (info) => (
          <Typography as="span" size="14" color="subtle">
            {info.getValue()}
          </Typography>
        ),
        header: () => toSentenceCase(LEAVE_SETTING_VALUE_TEXT(leaveType)),
        size: 1000,
      }),
      columnHelper.accessor('isApplied', {
        cell: (info) => (
          <Tag
            id="policy-status"
            intent={info.getValue() ? 'success' : 'default'}
          >
            {info.getValue() ? ACTIVE_TEXT : INACTIVE_TEXT}
          </Tag>
        ),
        header: () => ColumnHeaders.status,
      }),
      columnHelper.display({
        id: 'actions',
        cell: (info) => {
          const policyItem = info.row.original;
          // eslint-disable-next-line react/jsx-no-useless-fragment
          let actionButton = <></>;

          switch (policyItem.action) {
            case EmploymentTimeClientPolicyActionItem.APPLY: {
              actionButton = (
                <StatutoryActivationModal
                  country={policyItem.policy.country}
                  companyId={companyId}
                  trigger={
                    <Box minW={100}>
                      <Button variant="primary" size="small" fullWidth>
                        {ACTIVATE_TEXT}
                      </Button>
                    </Box>
                  }
                />
              );
              break;
            }
            case EmploymentTimeClientPolicyActionItem.EDIT: {
              actionButton = policyItem.policy.customMinimum ? (
                <LeaveSettingUpdateModal
                  policy={policyItem.policy}
                  companyId={companyId}
                  statutoryDescription={policyItem.statutoryDescription}
                  action={policyItem.isApplied ? 'edit' : 'activate'}
                  trigger={
                    <Box minW={100}>
                      <Button
                        variant={policyItem.isApplied ? 'secondary' : 'primary'}
                        size="small"
                      >
                        {policyItem.isApplied ? EDIT_TEXT : ACTIVATE_TEXT}
                      </Button>
                    </Box>
                  }
                />
              ) : (
                // eslint-disable-next-line react/jsx-no-useless-fragment
                <></>
              );
              break;
            }
          }
          return <Inline justify="end">{actionButton}</Inline>;
        },
        header: () => ColumnHeaders.action,
        meta: {
          loader: <Skeleton height={24} width={24} />,
        },
      }),
    ],
    [columnHelper, companyId, leaveType],
  );

  return { columns, columnHelper };
};
